<template>
  <div
    class="drr"
    :style="style"
    :class="classObject"
    @dblclick="dblclick($event)"
    @mousedown="bodyMouseDown($event)"
    @touchstart.stop.prevent="bodyMouseDown($event)"
  >
    <slot></slot>
    <div
      v-for="(stick, i) in sticks"
      class="drr-stick"
      :class="['drr-stick-' + stick, resizable ? '' : 'not-resizable']"
      @mousedown.stop.prevent="stickDown(stick, $event)"
      @touchstart.stop.prevent="stickDown(stick, $event)"
      :style="drrStick(stick)"
      :key="i"
      ref="stick"
    ></div>
    <!-- <div class="ro-stick-handle" v-if="rotatable"></div> -->
  </div>
</template>

<script>
import Vector from './vector';
import getCursor from '../cropper/getCursor';

// const stickSize = 8;
const stickSize = 12;
const roStickSize = 20;
const styleMapping = {
  y: {
    t: 'top',
    m: 'marginTop',
    b: 'bottom'
  },
  x: {
    l: 'left',
    m: 'marginLeft',
    r: 'right'
  }
};

export default {
  name: 'drr',
  props: {
    x: {
      type: Number,
      required: true,
      validator: function(val) {
        return typeof val === 'number';
      }
    },
    y: {
      type: Number,
      required: true,
      validator: function(val) {
        return typeof val === 'number';
      }
    },
    w: {
      type: Number,
      required: true,
      validator: function(val) {
        return val > 0;
      }
    },
    h: {
      type: Number,
      required: true,
      validator: function(val) {
        return val > 0;
      }
    },
    angle: {
      type: Number,
      default: 0,
      validator: function(val) {
        return typeof val === 'number';
      }
    },
    selected: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: true
    },
    draggable: {
      type: Boolean,
      default: true
    },
    resizable: {
      type: Boolean,
      default: true
    },
    rotatable: {
      type: Boolean,
      default: true
    },
    hasActiveContent: {
      type: Boolean,
      default: false
    },
    aspectRatio: {
      type: Boolean,
      default: false
    },
    dragHandle: {
      type: String,
      default: null
    },
    dragCancel: {
      type: String,
      default: null
    },
    outerBound: {
      type: Object
    },
    innerBound: {
      type: Object
    },

    alwaysActive: {
      type: Boolean,
      default: true
    },
    isLimit: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      active: this.selected,
      contentActive: false,
      cx: this.x,
      cy: this.y,
      width: this.w,
      height: this.h,
      rotation: this.angle,
      bodyDrag: false,
      dragged: false,
      resized: false,
      rotated: false,
    };
  },

  computed: {
    sticks() {
      let sticks = [];
      if (this.resizable) sticks.push('tl', 'tr', 'br', 'bl');
      sticks.push('tm', 'mr', 'bm', 'ml');
      if (this.rotatable) sticks.push('ro');
      return sticks;
    },

    classObject() {
      return {
        active: this.active,
        inactive: !this.active,
        dragging: this.bodyDrag,
        'content-active': this.contentActive
      };
    },

    style() {
      if (this.isLimit && this.$el && this.$el.offsetParent) {
        let cx = this.cx;
        let cy = this.cy;
        let offsetParentRect = this.$el.offsetParent.getBoundingClientRect();
        let $elRect = this.$el.getBoundingClientRect();
        let dx = $elRect.width / 2;
        let dy = $elRect.height / 2;
        if (cx > offsetParentRect.width - dx) {
          this.cx = offsetParentRect.width - dx;
        }
        if (cx < 0 + dx) {
          this.cx = 0 + dx;
        }
        if (cy > offsetParentRect.height - dy) {
          this.cy = offsetParentRect.height - dy;
        }
        if (cy < 0 + dy) {
          this.cy = 0 + dy;
        }
      }
      return {
        left: this.cx - this.width / 2 + 'px',
        top: this.cy - this.height / 2 + 'px',
        width: this.width + 'px',
        height: this.height + 'px',
        transform: 'rotate(' + this.rotation + 'deg)'
      };
    },

    drrStick() {
      return stick => {
        const stickStyle = {
          width: `${stickSize}px`,
          height: `${stickSize}px`
        };
        if (stick == 'ro') {
          // stickStyle['top'] = `${-stickSize / 2 - roStickSize}px`;
          // stickStyle['marginLeft'] = `${-stickSize / 2 + 1}px`;

          // custom
          stickStyle.top = this.height + stickSize / 2 + 'px';
          stickStyle.left = this.width + stickSize / 2 + 'px';
          stickStyle.width = roStickSize + 'px';
          stickStyle.height = roStickSize + 'px';
        }
        if (stick && stick.indexOf('m') > -1) {
          stickStyle.width = stickSize / 2 + 'px';
          stickStyle.height = stickSize / 2 + 'px';
        } else {
          stickStyle[styleMapping.y[stick[0]]] = `${-stickSize / 2}px`;
          stickStyle[styleMapping.x[stick[1]]] = `${-stickSize / 2}px`;
        }
        return stickStyle;
      };
    },

    rect() {
      return {
        x: this.cx,
        y: this.cy,
        w: this.width,
        h: this.height,
        angle: this.rotation
      };
    }
  },

  watch: {
    active(val) {
      if (val) this.$emit('select');
      else this.$emit('deselect');
      if (this.alwaysActive) {
        this.active = true;
      }
    },

    selected(val) {
      this.active = val;
      console.log({ active: val });
    },

    hasActiveContent: {
      handler: function(val) {
        if (val) {
          this.$on('content-active', this.onContentActive);
          this.$on('content-inactive', this.onContentInactive);
        } else {
          this.$off('content-active');
          this.$off('content-inactive');
          if (this.contentActive) this.onContentInactive();
        }
      },
      immediate: true
    },

    x() {
      if (this.stickDrag || this.bodyDrag) return;

      this.cx = this.x;
    },

    y() {
      if (this.stickDrag || this.bodyDrag) return;

      this.cy = this.y;
    },

    w() {
      if (this.stickDrag || this.bodyDrag) return;

      this.currentStick = ['m', 'r'];

      this.width = this.w;
    },

    h() {
      if (this.stickDrag || this.bodyDrag) return;

      this.currentStick = ['b', 'm'];

      this.height = this.h;
    },

    angle() {
      if (this.stickDrag || this.bodyDrag) return;

      this.rotation = this.angle;
    }
  },

  created: function() {
    this.stickDrag = false;
    this.bodyDrag = false;
    this.stickStartPos = { mouseX: 0, mouseY: 0, x: 0, y: 0, w: 0, h: 0 };

    this.currentStick = [];
  },

  mounted: function() {
    this.parentElement = this.$el.parentNode;
    // this.parentWidth = this.parentW ? this.parentW : this.parentElement.clientWidth;
    // this.parentHeight = this.parentH ? this.parentH : this.parentElement.clientHeight;

    document.documentElement.addEventListener('mousemove', this.move);
    document.documentElement.addEventListener('mouseup', this.up);
    document.documentElement.addEventListener('mouseleave', this.up);

    document.documentElement.addEventListener('mousedown', this.deselect);

    document.documentElement.addEventListener('touchmove', this.move, true);
    document.documentElement.addEventListener(
      'touchend touchcancel',
      this.up,
      true
    );
    document.documentElement.addEventListener('touchstart', this.up, true);

    if (this.dragHandle) {
      let dragHandles = Array.prototype.slice.call(
        this.$el.querySelectorAll(this.dragHandle)
      );
      for (let i in dragHandles) {
        dragHandles[i].setAttribute('data-drag-handle', this._uid);
      }
    }

    if (this.dragCancel) {
      let cancelHandles = Array.prototype.slice.call(
        this.$el.querySelectorAll(this.dragCancel)
      );
      for (let i in cancelHandles) {
        cancelHandles[i].setAttribute('data-drag-cancel', this._uid);
      }
    }

    this.setCursor();
  },

  beforeDestroy: function() {
    document.documentElement.removeEventListener('mousemove', this.move);
    document.documentElement.removeEventListener('mouseup', this.up);
    document.documentElement.removeEventListener('mouseleave', this.up);

    document.documentElement.removeEventListener('mousedown', this.deselect);

    document.documentElement.removeEventListener('touchmove', this.move, true);
    document.documentElement.removeEventListener(
      'touchend touchcancel',
      this.up,
      true
    );
    document.documentElement.removeEventListener('touchstart', this.up, true);
  },

  methods: {
    dblclick(e) {
      this.$emit('content-active');
    },

    onContentActive() {
      this.contentActive = true;
      this.active = false;
      for (const child of this.$children) {
        child.$emit('active');
      }
    },

    onContentInactive() {
      this.contentActive = false;
      this.active = true;
      for (const child of this.$children) {
        child.$emit('inactive');
      }
    },

    deselect() {
      this.$emit('deselect');
      this.active = false;
    },

    move(ev) {
      if (!this.stickDrag && !this.bodyDrag) {
        return;
      }

      ev.stopPropagation();

      if (this.stickDrag) {
        this.stickMove(ev);
      }
      if (this.bodyDrag) {
        this.bodyMove(ev);
      }
    },

    up(ev) {
      if (this.stickDrag) {
        this.stickUp(ev);
      }
      if (this.bodyDrag) {
        this.bodyUp(ev);
      }
    },

    bodyMouseDown: function(e) {
      if (this.contentActive || !this.selectable) {
        return;
      } else {
        e.stopPropagation();
        e.preventDefault();
      }

      let target = e.target || e.srcElement;

      this.active = true;

      if (e.button && e.button !== 0) {
        return;
      }

      this.$emit('clicked', e);

      if (!this.draggable || !this.active) {
        return;
      }

      if (
        this.dragHandle &&
        target.getAttribute('data-drag-handle') !== this._uid.toString()
      ) {
        return;
      }

      if (
        this.dragCancel &&
        target.getAttribute('data-drag-cancel') === this._uid.toString()
      ) {
        return;
      }

      this.bodyDrag = true;
      this.dragged = false;

      this.dragStartEmitted = false;
      this.startRect = { ...this.rect };

      this.stickStartPos.mouseX = e.pageX || e.touches[0].pageX;
      this.stickStartPos.mouseY = e.pageY || e.touches[0].pageY;

      this.stickStartPos.cx = this.cx;
      this.stickStartPos.cy = this.cy;
    },

    bodyMove(ev) {
      const stickStartPos = this.stickStartPos;

      const newPos = {
        mouseX: ev.pageX || ev.touches[0].pageX,
        mouseY: ev.pageY || ev.touches[0].pageY
      };
      const delta = {
        x: newPos.mouseX - stickStartPos.mouseX,
        y: newPos.mouseY - stickStartPos.mouseY
      };

      let newcx = stickStartPos.cx + delta.x;
      let newcy = stickStartPos.cy + delta.y;
      let x1 = newcx - this.width / 2;
      let y1 = newcy - this.height / 2;
      let x2 = newcx + this.width / 2;
      let y2 = newcy + this.height / 2;

      if (this.outerBound && this.rotation == 0) {
        let bx1 = this.outerBound.x - this.outerBound.w / 2;
        let by1 = this.outerBound.y - this.outerBound.h / 2;
        let bx2 = this.outerBound.x + this.outerBound.w / 2;
        let by2 = this.outerBound.y + this.outerBound.h / 2;
        if (x1 < bx1) delta.x -= x1 - bx1;
        if (x2 > bx2) delta.x -= x2 - bx2;
        if (y1 < by1) delta.y -= y1 - by1;
        if (y2 > by2) delta.y -= y2 - by2;
      }

      if (this.innerBound && this.rotation == 0) {
        let bx1 = this.innerBound.x - this.innerBound.w / 2;
        let by1 = this.innerBound.y - this.innerBound.h / 2;
        let bx2 = this.innerBound.x + this.innerBound.w / 2;
        let by2 = this.innerBound.y + this.innerBound.h / 2;
        if (x1 > bx1) delta.x -= x1 - bx1;
        if (x2 < bx2) delta.x -= x2 - bx2;
        if (y1 > by1) delta.y -= y1 - by1;
        if (y2 < by2) delta.y -= y2 - by2;
      }

      this.cx = stickStartPos.cx + delta.x;
      this.cy = stickStartPos.cy + delta.y;

      if (!this.dragStartEmitted) {
        this.$emit('dragstart', this.startRect);
        this.dragStartEmitted = true;
      }

      this.dragged = true;
      this.$emit('drag', this.rect);
    },

    bodyUp() {
      this.bodyDrag = false;
      if (this.dragged) {
        this.$emit('dragstop', this.rect, this.startRect);
        this.$emit('change', this.rect);
      }

      this.stickStartPos = { mouseX: 0, mouseY: 0, x: 0, y: 0, w: 0, h: 0 };
    },

    stickDown: function(stick, ev) {
      if (!this.resizable || !this.active) return;

      this.resizeStartEmitted = false;
      this.rotateStartEmitted = false;
      this.startRect = { ...this.rect };

      this.stickDrag = true;
      this.resized = false;
      this.rotated = false;
      this.stickStartPos.mouseX = ev.clientX;
      this.stickStartPos.mouseY = ev.clientY;
      this.stickStartPos.cx = this.cx;
      this.stickStartPos.cy = this.cy;
      this.stickStartPos.width = this.width;
      this.stickStartPos.height = this.height;
      this.stickStartPos.rotation = this.rotation;

      this.currentStick = stick;
    },

    stickMove(ev) {
      const stickStartPos = this.stickStartPos;
      let mouseX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      let mouseY = ev.touches ? ev.touches[0].clientY : ev.clientY;

      let delta = new Vector(
        mouseX - stickStartPos.mouseX,
        mouseY - stickStartPos.mouseY
      );

      if (this.currentStick == 'ro') {
        let offsetParentRect = this.$el.offsetParent.getBoundingClientRect();
        let pointerX = this.cx + offsetParentRect.left;
        let pointerY = this.cy + offsetParentRect.top;

        let rad1 = Math.atan2(mouseY - pointerY, mouseX - pointerX);
        let rad2 = Math.atan2(stickStartPos.mouseY - pointerY, stickStartPos.mouseX - pointerX);

        let targetDeg = ((rad1 - rad2) * 180) / Math.PI + stickStartPos.rotation;

        if (!this.rotateStartEmitted) {
          this.$emit('rotatestart', this.startRect);
          this.rotateStartEmitted = true;
        }

        this.rotation = targetDeg;
        this.rotated = true;
        this.$emit('rotate', this.rect);
      } else {
        let isCorner = ['tl', 'tr', 'br', 'bl'].indexOf(this.currentStick) > -1;
        let dirX = this.currentStick[1] == 'l' ? -1 : 1;
        let dirY = this.currentStick[0] == 't' ? -1 : 1;

        let phi = Vector.rad(stickStartPos.rotation);
        let p;
        if (this.aspectRatio && isCorner) {
          let axis = new Vector(
            (dirX * stickStartPos.width) / 2,
            (dirY * stickStartPos.height) / 2
          );
          axis = axis.rotate(phi).unit();
          p = axis.mul(axis.mul(delta));
        } else {
          p = delta;
        }

        let pn = p.rotate(-phi);

        let newcx = stickStartPos.cx + p.x / 2;
        let newcy = stickStartPos.cy + p.y / 2;
        let newwidth = stickStartPos.width + dirX * pn.x;
        let newheight = stickStartPos.height + dirY * pn.y;
        let x1 = newcx - newwidth / 2;
        let y1 = newcy - newheight / 2;
        let x2 = newcx + newwidth / 2;
        let y2 = newcy + newheight / 2;

        if (this.outerBound && this.rotation == 0) {
          let bx1 = this.outerBound.x - this.outerBound.w / 2;
          let by1 = this.outerBound.y - this.outerBound.h / 2;
          let bx2 = this.outerBound.x + this.outerBound.w / 2;
          let by2 = this.outerBound.y + this.outerBound.h / 2;
          let dx = 0;
          let dy = 0;
          if (x1 < bx1) dx = bx1 - x1;
          if (x2 > bx2) dx = bx2 - x2;
          if (y1 < by1) dy = by1 - y1;
          if (y2 > by2) dy = by2 - y2;

          if (dx != 0 || dy != 0) {
            if (this.aspectRatio) {
              if (dx / p.x < dy / p.y) {
                p.y += (dx * p.y) / p.x;
                p.x += dx;
              } else {
                p.x += (dy * p.x) / p.y;
                p.y += dy;
              }
            } else {
              p.x += dx;
              p.y += dy;
            }
          }
        }

        if (this.innerBound && this.rotation == 0) {
          let bx1 = this.innerBound.x - this.innerBound.w / 2;
          let by1 = this.innerBound.y - this.innerBound.h / 2;
          let bx2 = this.innerBound.x + this.innerBound.w / 2;
          let by2 = this.innerBound.y + this.innerBound.h / 2;
          let dx = 0;
          let dy = 0;
          if (x1 > bx1) dx = bx1 - x1;
          if (x2 < bx2) dx = bx2 - x2;
          if (y1 > by1) dy = by1 - y1;
          if (y2 < by2) dy = by2 - y2;

          if (dx != 0 || dy != 0) {
            if (this.aspectRatio) {
              if (dx / p.x < dy / p.y) {
                p.y += (dx * p.y) / p.x;
                p.x += dx;
              } else {
                p.x += (dy * p.x) / p.y;
                p.y += dy;
              }
            } else {
              p.x += dx;
              p.y += dy;
            }
          }
        }

        if (this.currentStick === 'mr' || this.currentStick === 'ml') {
          pn.y = 0;
          this.width = stickStartPos.width + dirX * pn.x;
          // this.height = this.width / (stickStartPos.width / stickStartPos.height);
          // this.cx = stickStartPos.cx + (pn.x / 2) * Math.cos(phi);
          // this.cy = stickStartPos.cy + (pn.x / 2) * Math.sin(phi);
          this.cx = stickStartPos.cx + pn.rotate(phi).x / 2;
          this.cy = stickStartPos.cy + pn.rotate(phi).y / 2;
        }
        if (this.currentStick === 'tm' || this.currentStick === 'bm') {
          pn.x = 0;
          this.height = stickStartPos.height + dirY * pn.y;
          // this.width = this.height * (stickStartPos.width / stickStartPos.height);
          // this.cx = stickStartPos.cx + (pn.y / 2) * Math.cos(phi + Math.PI / 2);
          // this.cy = stickStartPos.cy + (pn.y / 2) * Math.sin(phi + Math.PI / 2);
          this.cx = stickStartPos.cx + pn.rotate(phi).x / 2;
          this.cy = stickStartPos.cy + pn.rotate(phi).y / 2;
        }

        if (isCorner) {
          this.cx = stickStartPos.cx + p.x / 2;
          this.cy = stickStartPos.cy + p.y / 2;
          // pn = p.rotate(-phi)
          this.width = stickStartPos.width + dirX * pn.x;
          this.height = stickStartPos.height + dirY * pn.y;
        }

        if (this.width < 10) {
          this.width = 10;
          limitSize.call(this);
        }
        if (this.height < 10) {
          this.height = 10;
          limitSize.call(this);
        }
        function limitSize () {
          let pnn = new Vector(
            pn.x / Math.abs(pn.x || 1) * Math.abs(this.width - stickStartPos.width),
            pn.y / Math.abs(pn.y || 1) * Math.abs(this.height - stickStartPos.height)
          );
          pnn = pnn.rotate(phi);
          this.cx = stickStartPos.cx + pnn.x / 2;
          this.cy = stickStartPos.cy + pnn.y / 2;
        }

        if (this.resizeHandler)
          this.setRect(this.resizeHandler(this.getRect()));

        if (!this.resizeStartEmitted) {
          this.$emit('resizestart', this.startRect);
          this.resizeStartEmitted = true;
        }

        this.resized = true;
        this.$emit('resize', this.rect);
      }
    },

    stickUp() {
      this.stickDrag = false;
      this.stickStartPos = {
        mouseX: 0,
        mouseY: 0,
        x: 0,
        y: 0,
        w: 0,
        h: 0
      };

      if (this.resized) {
        this.$emit('resizestop', this.rect, this.startRect); // TODO
        this.$emit('change', this.rect);
      }

      if (this.rotated) {
        this.$emit('rotatestop', this.rect, this.startRect);
        this.$emit('change', this.rect);
        this.setCursor();
      }
    },

    setCursor() {
      let rect = this.rect;
      let r = rect.angle;
      if (r < 0) {
        r = 360 + r;
      }
      // let count = Math.floor(r / 45);
      // let arr = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
      // let newArr = arr.slice(0);
      // for (let i = 0; i < count; i++) {
      //   newArr.push(newArr.shift());
      // }

      let arrName = ['nw', 'ne', 'se', 'sw', 'n', 'e', 's', 'w'];
      for (let i = 0; i < 8; i += 1) {
        let newName = getCursor(r, arrName[i]);
        this.$refs.stick[i].style.cursor = `${newName}-resize`;
      }
    }
  }
};
</script>

<style scoped>
/*TODO less */

.drr {
  position: absolute;
  box-sizing: border-box;
  cursor: pointer;
}

.drr:hover:before {
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
  outline: 2px dashed #d6d6d6;
}

.drr.active:before {
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
  outline: 2px dashed lightskyblue;
}

.drr-stick {
  box-sizing: border-box;
  position: absolute;
  font-size: 1px;
  background: #ffffff;
  border: 1px solid #6c6c6c;
  box-shadow: 0 0 2px #bbb;
}

.drr-stick:hover {
  border-color: lightskyblue;
}

.inactive > .drr-stick {
  display: none;
}

.drr-stick-tl,
.drr-stick-br {
  cursor: nwse-resize;
}

.drr-stick-tm,
.drr-stick-bm {
  left: 50%;
  cursor: ns-resize;
}
.drr-stick-tm {
  top: 0;
  transform: translate(-50%, -50%);
}
.drr-stick-bm {
  top: 100%;
  transform: translate(-50%, -50%);
}

.drr-stick-tr,
.drr-stick-bl {
  cursor: nesw-resize;
}

.drr-stick-ml,
.drr-stick-mr {
  top: 50%;
  cursor: ew-resize;
}
.drr-stick-ml {
  left: 0;
  transform: translate(-50%, -50%);
}
.drr-stick-mr {
  left: 100%;
  transform: translate(-50%, -50%);
}

.drr-stick-ro {
  left: 50%;
  cursor: ew-resize;
  border-radius: 4px;
}

.ro-stick-handle {
  left: 50%;
  top: -16px;
  box-sizing: border-box;
  position: absolute;
  font-size: 1px;
  background: #ffffff;
  border: 1px solid #6c6c6c;
  box-shadow: 0 0 2px #bbb;
  width: 0px;
  height: 16px;
}

.inactive > .ro-stick-handle {
  display: none;
}

.drr-stick.not-resizable {
  display: none;
}

.content-active {
  border: 2px solid lightskyblue; /*TODO*/
}

/* custom */
.drr.active {
  outline: none;
  border: transparent 1px solid;
  /* margin-top: -1px;
    margin-left: -1px; */
  border-image-source: url('data:image/gif;base64,R0lGODlhCgAKAJECAAAAAP///////wAAACH/C05FVFNDQVBFMi4wAwEAAAAh/wtYTVAgRGF0YVhNUDw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6OEI5RDc5MTFDNkE2MTFFM0JCMDZEODI2QTI4MzJBOTIiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6OEI5RDc5MTBDNkE2MTFFM0JCMDZEODI2QTI4MzJBOTIiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoTWFjaW50b3NoKSI+IDx4bXBNTTpEZXJpdmVkRnJvbSBzdFJlZjppbnN0YW5jZUlEPSJ4bXAuZGlkOjAyODAxMTc0MDcyMDY4MTE4MDgzQzNDMjA5MzREQ0ZDIiBzdFJlZjpkb2N1bWVudElEPSJ4bXAuZGlkOjAyODAxMTc0MDcyMDY4MTE4MDgzQzNDMjA5MzREQ0ZDIi8+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+Af/+/fz7+vn49/b19PPy8fDv7u3s6+rp6Ofm5eTj4uHg397d3Nva2djX1tXU09LR0M/OzczLysnIx8bFxMPCwcC/vr28u7q5uLe2tbSzsrGwr66trKuqqainpqWko6KhoJ+enZybmpmYl5aVlJOSkZCPjo2Mi4qJiIeGhYSDgoGAf359fHt6eXh3dnV0c3JxcG9ubWxramloZ2ZlZGNiYWBfXl1cW1pZWFdWVVRTUlFQT05NTEtKSUhHRkVEQ0JBQD8+PTw7Ojk4NzY1NDMyMTAvLi0sKyopKCcmJSQjIiEgHx4dHBsaGRgXFhUUExIREA8ODQwLCgkIBwYFBAMCAQAAIfkEBQoAAgAsAAAAAAoACgAAAhWEERkn7W3ei7KlagMWF/dKgYeyGAUAIfkEBQoAAgAsAAAAAAoACgAAAg+UYwLJ7RnQm7QmsCyVKhUAIfkEBQoAAgAsAAAAAAoACgAAAhCUYgLJHdiinNSAVfOEKoUCACH5BAUKAAIALAAAAAAKAAoAAAIRVISAdusPo3RAzYtjaMIaUQAAIfkEBQoAAgAsAAAAAAoACgAAAg+MDiem7Q8bSLFaG5il6xQAIfkEBQoAAgAsAAAAAAoACgAAAg+UYRLJ7QnQm7SmsCyVKhUAIfkEBQoAAgAsAAAAAAoACgAAAhCUYBLJDdiinNSEVfOEKoECACH5BAUKAAIALAAAAAAKAAoAAAIRFISBdusPo3RBzYsjaMIaUQAAOw==');
  border-image-slice: 1 1 1 1;
  border-image-width: initial;
  border-image-outset: initial;
  border-image-repeat: repeat;
}
.drr.active:before {
  outline: none;
}
.drr-stick {
  background-color: #474261;
  border-radius: 50%;
  border: 1px solid #fff;
  opacity: 1;
}
.drr-stick-ro {
  cursor: grab;
  background-color: transparent;
  border: none;
  box-shadow: none;

  background-image: url(data:image/svg+xml;base64,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);
}
</style>



// WEBPACK FOOTER //
// src/plugins/drr/index.vue